import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useApi } from '@/composables/useApi'
import { storage } from '@/composables/useStorage'
import { useCartStore } from '@/stores/cart'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(null)
  const { post, put } = useApi()

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const hasCity = computed(() => {
    // Проверяем город из пользователя или localStorage
    if (user.value?.city) return true
    try {
      const savedCity = storage.get('selected_city')
      return !!savedCity
    } catch (e) {
      return false
    }
  })
  const currentUser = computed(() => user.value)

  // Определение города по поддомену
  const detectCityFromSubdomain = () => {
    if (typeof window === 'undefined') return null
    
    const hostname = window.location.hostname
    
    // Проверяем, не является ли хост IP-адресом
    const isIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)
    if (isIp) return null

    // Извлекаем поддомен (например, izhevsk.maxrolls.ru -> izhevsk)
    const parts = hostname.split('.')
    
    // Если это не localhost и есть поддомен перед основным доменом
    if (parts.length >= 3 && !hostname.includes('localhost') && !hostname.includes('127.0.0.1')) {
      const subdomain = (parts[0] || '').toLowerCase()
      // Игнорируем служебные поддомены
      if (subdomain !== 'www' && subdomain !== 'api' && subdomain !== 'admin') {
        // Поддомен -> название города как в БД (важно: в БД города на русском)
        const cityBySubdomain = {
          izhevsk: 'Ижевск',
          sarapul: 'Сарапул',
          neftekamsk: 'Нефтекамск',
          leninogorsk: 'Лениногорск',
          belebey: 'Белебей',
          elabuga: 'Елабуга',
          aznakaevo: 'Азнакаево',
          bugulma: 'Бугульма',
        }

        // Если поддомен не известен — лучше вернуть null и заставить пользователя выбрать город,
        // чем сохранить неверное значение и получать "Меню временно недоступно".
        return cityBySubdomain[subdomain] || null
      }
    }
    
    return null
  }

  // Инициализация - проверка сохраненного токена и определение города по поддомену
  const init = async () => {
    const savedToken = storage.get('auth_token')
    const savedUser = storage.get('auth_user')
    
    // Определяем город по поддомену
    const subdomainCity = detectCityFromSubdomain()
    if (subdomainCity) {
      // Сохраняем город из поддомена, если его еще нет
      const currentCity = savedUser?.city || storage.get('selected_city')
      if (!currentCity || currentCity !== subdomainCity) {
        storage.set('selected_city', subdomainCity)
        // Если пользователь авторизован, обновляем город через API
        if (savedToken && savedUser) {
          try {
            await updateCity(subdomainCity)
          } catch (e) {
            console.warn('[Auth] Failed to update city from subdomain:', e)
          }
        }
      }
    }
    
    if (savedToken && savedUser) {
      token.value = savedToken
      user.value = savedUser
      return true
    }
    
    return false
  }

  // Авторизация через Telegram WebApp
  const loginWithTelegram = async (initData) => {
    try {
      const response = await post('auth/telegram', {
        initData: initData
      })

      if (response.success && response.token && response.user) {
        token.value = response.token
        user.value = response.user
        
        // Сохраняем в localStorage
        storage.set('auth_token', response.token)
        storage.set('auth_user', response.user)
        
        // Синхронизируем локальную корзину с серверной
        const cartStore = useCartStore()
        await cartStore.syncLocalCartToServer()
        
        return true
      }
      
      throw new Error(response.message || 'Ошибка авторизации')
    } catch (error) {
      console.error('[Auth] Login error:', error)
      throw error
    }
  }

  // Авторизация по телефону
  const loginWithPhone = async (phone, code) => {
    try {
      const response = await post('auth/phone', {
        phone: phone,
        code: code
      })

      if (response.success && response.token && response.user) {
        token.value = response.token
        user.value = response.user
        
        storage.set('auth_token', response.token)
        storage.set('auth_user', response.user)
        
        // Синхронизируем локальную корзину с серверной
        const cartStore = useCartStore()
        await cartStore.syncLocalCartToServer()
        
        return true
      }
      
      throw new Error(response.message || 'Ошибка авторизации')
    } catch (error) {
      console.error('[Auth] Phone login error:', error)
      throw error
    }
  }

  // Отправка кода на телефон
  const sendPhoneCode = async (phone) => {
    try {
      // Код приходит в Telegram: для существующего аккаунта это обязательное
      // подтверждение владения номером, для нового — первый шаг регистрации.
      const response = await post('auth/phone/request', {
        phone: phone
      })

      return response.success
    } catch (error) {
      console.error('[Auth] Send code error:', error)
      throw error
    }
  }

  // Проверка авторизации (валидация токена)
  const checkAuth = async () => {
    if (!token.value) {
      return false
    }

    try {
      const response = await post('auth/check')

      if (response.success && response.user) {
        user.value = response.user
        storage.set('auth_user', response.user)
        return true
      }
      
      // Токен невалиден, очищаем
      logout()
      return false
    } catch (error) {
      console.error('[Auth] Check auth error:', error)
      logout()
      return false
    }
  }

  // Обновление города пользователя
  const updateCity = async (city) => {
    // Сохраняем город в localStorage сразу (для работы без авторизации)
    storage.set('selected_city', city)
    
    // Если пользователь авторизован, обновляем через API
    if (token.value && user.value) {
      try {
        const response = await put('user/profile', { city })
        if (response.success && response.user) {
          user.value = response.user
          storage.set('auth_user', response.user)
        }
        return true
      } catch (error) {
        console.error('[Auth] Update city error:', error)
        // Не бросаем ошибку, т.к. город уже сохранен в localStorage
      }
    }
    
    return true
  }

  // Обновление телефона
  const updatePhone = async (phone) => {
    try {
      const response = await put('user/profile', { phone })

      if (response.success && response.user) {
        user.value = response.user
        storage.set('auth_user', response.user)
        return true
      }
      
      throw new Error(response.message || 'Ошибка обновления телефона')
    } catch (error) {
      console.error('[Auth] Update phone error:', error)
      throw error
    }
  }

  // Выход
  const logout = () => {
    token.value = null
    user.value = null
    storage.remove('auth_token')
    storage.remove('auth_user')
  }

  return {
    // State
    user,
    token,
    // Computed
    isAuthenticated,
    hasCity,
    currentUser,
    // Actions
    init,
    loginWithTelegram,
    loginWithPhone,
    sendPhoneCode,
    checkAuth,
    updateCity,
    updatePhone,
    logout
  }
})

